import classNames from 'classnames'
import Table from '../../core/table'
import ContentLoader from '../../core/loader'
import NotFound from '../../../views/resources/NotFound'
import {ImgButton} from '../../core/button'
import EmptyContent from '../../../views/resources/EmptyContent'
import {EditIcon, GoodNewsIcon, HorizontalMoreIcon, LoadMoreIcon} from '../../../icons'
import {colors} from '../../../utils/colors'
import TextHelper from '../../../utils/text'
import Dropdown from '../../core/dropdown'
import {MenuItem} from '@szhsin/react-menu'
import Spinner from '../../core/spinner'
import {DateFormats, formatDate} from '../../../utils/date'

const IncidentsTable = ({
    incidents,
    getAssigneesList,
    sortedIncidents,
    setSortedIncidents,
    checkedIncidentsIds,
    setCheckedIncidentsIds,
    isFetching,
    isLoadMoreEnabled,
    isButtonLoaderActive,
    onLoadMoreIncidents,
    text,
    isDarkMode,
    onOpenEditModal,
    isProcessing
}) => {
    const tableHeader = [
        {
            label: text.urgency,
            className: 'min-width-150',
            sorting: {
                asc: {
                    key: 'urgency_asc',
                    field: ['urgency']
                },
                desc: {
                    key: 'urgency_desc',
                    field: ['urgency']
                }}
        },
        {
            label:
            text.name_placeholder,
            className: 'min-width-250',
            sorting: {
                asc: {
                    key: 'name_asc',
                    field: ['name']
                },
                desc: {
                    key: 'name_desc',
                    field: ['name']
                }}},
        {
            label: text.description_placeholder,
            className: 'min-width-400',
            sorting: {
                asc: {
                    key: 'description_asc',
                    field: ['description']
                },
                desc: {
                    key: 'description_desc',
                    field: ['description']
                }}},
        {
            label: text.status,
            className: 'min-width-150',
            sorting: {
                asc: {
                    key: 'status_asc',
                    field: ['status']
                },
                desc: {
                    key: 'status_desc',
                    field: ['status']
                }}},
        {
            label: text.assignees,
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'assignees_asc',
                    field: ['assignees']
                },
                desc: {
                    key: 'assignees_desc',
                    field: ['assignees']
                }}},
        {
            label: text.created_at,
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'created_at_asc',
                    field: ['created_at']
                },
                desc: {
                    key: 'created_at_desc',
                    field: ['created_at']
                }}},
        {
            label: text.updated_at,
            className: 'flex-1 justify-content-flex-start gap-12',
            sorting: {
                asc: {
                    key: 'updated_at_asc',
                    field: ['updated_at']},
                desc: {
                    key: 'updated_at_desc',
                    field: ['updated_at']
                }}}
    ]

    const renderRow = sortedIncidents?.map((item) => {
        const createdAt = formatDate(item?.created_at, DateFormats.DASH_YMD_TIME_SHORT)
        const updatedAt = formatDate(item?.updated_at, DateFormats.DASH_YMD_TIME_SHORT)
        const isSelected = checkedIncidentsIds.includes(item?.id) && isProcessing

        return [
            <span className="min-width-150" key="urgency">
                <span className={classNames(`badge urgency ${item?.urgency} capitalize`, {dark: isDarkMode})}>
                    {item?.urgency}
                </span>
            </span>,
            <span className="min-width-250" title={item?.name} key="name">
                {TextHelper.textLengthChecker(item?.name, 60) || '-'}
            </span>,
            <span className="min-width-400" title={item?.description} key="description">
                {TextHelper.textLengthChecker(item?.description, 60) || '-'}
            </span>,
            <span className="min-width-150" key="status">
                {isSelected ? (
                    <Spinner classname="ml-0" />
                ) : (
                    <span className={classNames(`badge status ${item?.status} capitalize`, {dark: isDarkMode})}>
                        {item?.status}
                    </span>
                )}
            </span>,
            <span className="min-width" key="assignees">
                {(item?.assignees?.length && getAssigneesList(item.assignees)) || text.unassigneed}
            </span>,
            <span className="min-width" key="created">
                {createdAt}
            </span>,
            <span className="flex-1" key="updated">
                <span>{updatedAt === createdAt ? text.not_available : updatedAt}</span>
                {item?.trigger_type === 'manual' && item?.status !== 'resolved' && (
                    <Dropdown
                        menuButton={
                            <button className={classNames('table-action-btn', {
                                dark: isDarkMode
                            })}>
                                <HorizontalMoreIcon />
                            </button>
                        }
                    >
                        <MenuItem onClick={() => onOpenEditModal(item?.id)}>
                            <span className="d-flex"><EditIcon /></span>
                            <span>{text.edit}</span>
                        </MenuItem>
                    </Dropdown>
                )}
            </span>
        ]
    })

    const customFieldLogic = {
        assignees: (item) => item?.assignees?.[0]?.name || text.unassigneed
    }

    if (isFetching) return <ContentLoader />

    if (!incidents?.length) {
        return (
            <NotFound
                message={text.not_found_incidents}
                icon={<GoodNewsIcon className="good-news-icon" />}
            />
        )
    }

    if (!sortedIncidents?.length) {
        return (
            <EmptyContent
                message={text.empty_content_text}
                hideButton={true}
            />
        )
    }

    return (
        <>
            <div className={classNames('content-body', {
                dark: isDarkMode
            })}>
                <Table
                    tableHeader={tableHeader}
                    tbodyData={renderRow}
                    data={sortedIncidents}
                    setData={setSortedIncidents}
                    identificator="id"
                    inputName="id"
                    selectedData={checkedIncidentsIds}
                    isMultiCheckable={false}
                    hasCheckbox={true}
                    setSelectedData={setCheckedIncidentsIds}
                    isSortingEnabled
                    sortId="incidents"
                    customFieldLogic={customFieldLogic}
                />
            </div>

            {isLoadMoreEnabled && (
                <ImgButton
                    onClick={onLoadMoreIncidents}
                    type="button"
                    className={classNames('load-button', {
                        active: isButtonLoaderActive
                    })}
                >
                    <LoadMoreIcon stroke={colors.colorMuted} />
                    {text.load_more}
                </ImgButton>
            )}
        </>
    )
}

export default IncidentsTable
